import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Alert from "../../../Common/Alert";
import DatatableFunc from "../../../Common/DatatableFunc";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import ReactDataTable from "../../../Common/ReactDataTable";
import {
  CashCountStyles,
  DENOMINATIONS,
  formatDate,
  money,
  num,
} from "./CashCountShared";

const List = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [receiveData, setReceiveData] = useState([]);
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await Http.get(`/cash-count`);
      setReceiveData(res.data);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    DatatableFunc();
    fetchData();
  }, []);

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 4000);
  };

  useEffect(() => {
    if (!location.state) {
      return;
    }
    showAlert(location.state.message, location.state.type);
  }, [location]);

  const deleteData = (id) => {
    Swal.fire({
      title: "Delete this cash count?",
      text: "This record and its denomination breakdown will be removed permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#2a2962",
      cancelButtonColor: "#e42320",
      confirmButtonText: "Yes, delete it",
    }).then((result) => {
      if (result.isConfirmed) {
        Http.delete("/cash-count/" + id).then(() => {
          // destroy() returns every row unsorted, so refetch to keep the
          // newest-first ordering the list is built around.
          fetchData();
          Swal.fire("Deleted!", "The cash count has been deleted.", "success");
        });
      }
    });
  };

  const summary = useMemo(() => {
    const rows = receiveData || [];
    const total = rows.reduce((sum, row) => sum + num(row.total_amount), 0);
    const suspense = rows.reduce((sum, row) => sum + num(row.suspense), 0);
    return {
      count: rows.length,
      total,
      suspense,
      average: rows.length ? total / rows.length : 0,
      latest: rows.length ? rows[0].date : null,
    };
  }, [receiveData]);

  const dateBodyTemplate = (rowData) => (
    <span style={{ fontWeight: 600, color: "#2a2962" }}>
      {formatDate(rowData.date)}
    </span>
  );

  const denominationBodyTemplate = (field) => {
    const Component = (rowData) => {
      const value = num(rowData[field]);
      return (
        <span className={`cc-pill ${value ? "cc-pill-value" : "cc-pill-muted"}`}>
          {money(value)}
        </span>
      );
    };
    Component.displayName = `DenominationBodyTemplate(${field})`;
    return Component;
  };

  const suspenseBodyTemplate = (rowData) => {
    const value = num(rowData.suspense);
    return (
      <span className={`cc-pill ${value ? "cc-pill-alert" : "cc-pill-muted"}`}>
        {money(value)}
      </span>
    );
  };

  const totalBodyTemplate = (rowData) => (
    <span
      className="cc-num"
      style={{ fontWeight: 700, color: "#2a2962", display: "block" }}
    >
      ৳ {money(rowData.total_amount)}
    </span>
  );

  const actionTemplate = (item) => (
    <div className="d-flex gap-1">
      <button
        className="btn btn-outline-secondary btn-sm"
        onClick={() => navigate(`/view-cash-count/${item.id}`)}
        title="View details"
      >
        <i className="fa fa-eye"></i>
      </button>
      <button
        className="btn btn-outline-secondary btn-sm"
        onClick={() => navigate(`/edit-cash-count/${item.id}`)}
        title="Edit cash count"
      >
        <i className="fa fa-pencil"></i>
      </button>
      <button
        className="btn btn-outline-danger btn-sm"
        onClick={() => deleteData(item.id)}
        title="Delete cash count"
      >
        <i className="fa fa-trash"></i>
      </button>
    </div>
  );

  const cols = [
    {
      field: "date",
      header: "Date",
      body: dateBodyTemplate,
      sortable: true,
    },
    ...DENOMINATIONS.map((d) => ({
      field: d.total,
      header: `৳${d.label}`,
      body: denominationBodyTemplate(d.total),
      bodyClassName: "cc-num",
      headerClassName: "cc-num",
      sortable: true,
    })),
    {
      field: "suspense",
      header: "Suspense",
      body: suspenseBodyTemplate,
      bodyClassName: "cc-num",
      sortable: true,
    },
    {
      field: "total_amount",
      header: "Net Total",
      body: totalBodyTemplate,
      bodyClassName: "cc-num",
      sortable: true,
    },
  ];

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <CashCountStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">Cash Count</h5>
            <p className="text-muted mb-0 tx-12">
              Daily petty cash counts with the full denomination breakdown
            </p>
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary"
              onClick={fetchData}
              title="Reload data"
            >
              <i className="fa fa-refresh"></i>
            </button>
            <Link to="/create-cash-count" className="btn btn-primary">
              <i className="fa fa-plus-square mg-r-6"></i>New Cash Count
            </Link>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="row mg-b-20">
            <div className="col-md-3 mg-b-10">
              <div className="cc-stat">
                <i className="fa fa-list-ul cc-stat-icon"></i>
                <span className="cc-stat-label">Total Records</span>
                <span className="cc-stat-value">{summary.count}</span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="cc-stat">
                <i className="fa fa-money cc-stat-icon"></i>
                <span className="cc-stat-label">Total Counted</span>
                <span className="cc-stat-value">৳ {money(summary.total)}</span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="cc-stat is-alert">
                <i className="fa fa-exclamation-triangle cc-stat-icon"></i>
                <span className="cc-stat-label">Total Suspense</span>
                <span className="cc-stat-value">
                  ৳ {money(summary.suspense)}
                </span>
              </div>
            </div>
            <div className="col-md-3 mg-b-10">
              <div className="cc-stat">
                <i className="fa fa-calculator cc-stat-icon"></i>
                <span className="cc-stat-label">Average Per Count</span>
                <span className="cc-stat-value">
                  ৳ {money(summary.average)}
                </span>
              </div>
            </div>
          </div>

          <div className="cc-panel">
            <div className="cc-panel-head">
              <span>
                <i className="fa fa-table me-2"></i>Count History
              </span>
              {summary.latest && (
                <span className="cc-head-note">
                  Last count: {formatDate(summary.latest)}
                </span>
              )}
            </div>
            <div className="cc-panel-body">
              <ReactDataTable
                cols={cols}
                receiveData={receiveData}
                isLoading={loading}
                actionTemplate={actionTemplate}
                deleteData={deleteData}
                disablePDF={false}
                disableCSV={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default List;
